import assign from 'lodash/assign';
import pick from 'lodash/pick';
import toPlainObject from 'lodash/toPlainObject';

// Actions
import { buildSimilarFloorplansFilter } from '@zg-rentals/rental-growth-api';
import { getComparables } from './AddressActions';

// Models
import Filter from '../../models/Filter';
import type { PartialFilterType } from '../../models/Types';

// Misc / Utils
import { getFilterDiff, partialFilterToFilterParams } from '../../utils/filterUtils';
import { augmentError } from '../../utils/errors';
import constants from '../constants';
import type { AppThunkFilter, AppThunkPromise, AppThunkVoid } from '../types';

/**
 * All filter changes go through `handleFilterChange`. Updating or resetting a filter
 * will initially result in a url change. The PriceMyRentalResults page will listen for
 * url filter param changes and will call `loadFilterFromUrl`. `loadFilterFromUrl` will
 * parse the url filter params and ultimately update the filter on the store.
 *
 * This flow guarantees that the url w0ill be sharable and that our filter UI reflects
 * the filter in the url and store.
 */
export const handleFilterChange = (partialFilter?: PartialFilterType): AppThunkPromise => {
  return async (dispatch, getState) => {
    try {
      const { filter: currentFilter, address } = getState();
      let partialFilterForUrl = {};
      const savePrice = !address.rentZestimate && !partialFilter && currentFilter.price;

      if (savePrice) {
        partialFilterForUrl = { price: currentFilter.price };
      }

      if (partialFilter) {
        partialFilterForUrl = _getPartialFilterForUrl(currentFilter, partialFilter);
      }

      const { filterForGraphQL } = partialFilterToFilterParams(partialFilterForUrl);

      await dispatch(
        getComparables({
          street: address.street ?? '',
          city: address.city ?? '',
          state: address.state ?? '',
          zip: address.zip ?? '',
          ...(address.unit != null && { unit: address.unit }),
          similarFloorplansFilter: buildSimilarFloorplansFilter({
            // the address price will be overwritten by the price filter (if one is set)
            price: address.rentZestimate,
            ...filterForGraphQL,
          }),
        }),
      );

      const filter = dispatch(_updateFilter(partialFilterForUrl));

      return filter;
    } catch (error) {
      augmentError(error, {
        action: 'Error in FilterActions#handleFilterChange',
        partialFilter,
      });

      throw error;
    }
  };
};

export const resetFilters = (): AppThunkVoid => {
  return (dispatch) => {
    return dispatch(_updateFilter({}));
  };
};

const _getPartialFilterForUrl = (currentFilter, partialFilter?: PartialFilterType): PartialFilterType => {
  const newFilter = toPlainObject(new Filter(assign({}, currentFilter, partialFilter)));

  // get relevant filters, ie: non-default filters
  const filterDiff = getFilterDiff({
    baseFilter: toPlainObject(new Filter()),
    editedFilter: newFilter,
  });

  /**
   * When default values are truthy (ex: propertyTypes) filterDiff will only include
   * falsey values, ex: { propertyType : { house: false, townhouse: false } }. Since
   * we actually want url params to contain truthy propertyTypes
   * (propertyType=apartments-condos), we need to use the entire filter.
   *
   * Exception: to support activatedDays & deactivatedDays filters, we need to pass
   * both default (truthy) values for activeTypes
   */
  const partialFilterForUrl = pick(
    newFilter,
    Object.keys({
      ...filterDiff,
      activeTypes: null,
    }),
  );
  return partialFilterForUrl;
};

const _updateFilter = (partialFilter: PartialFilterType): AppThunkFilter => {
  return (dispatch): Filter => {
    try {
      const updatedFilter = new Filter(assign(toPlainObject(new Filter()), partialFilter));
      dispatch({
        type: constants.UPDATE_FILTER,
        payload: new Filter(assign(toPlainObject(new Filter()), partialFilter)),
      });
      return updatedFilter;
    } catch (error) {
      augmentError(error, {
        action: 'Error in FilterActions#_updateFilter',
        partialFilter: partialFilter,
      });

      throw error;
    }
  };
};
